import { redirect, replace, type RouteObject } from "react-router";
import { AppShell } from "./layout/AppShell";
import { deferColdStartPath, ensureBootstrapped } from "./bootstrap";
import { guestOnly, requireMember, requireOnboarding } from "./guards";
import { platform } from "@/platform";
import { SplashPage } from "@/features/system/SplashPage";
import { SPLASH_MOTION_SECONDS } from "@/features/system/splashMotion";
import { BlockedPage } from "@/features/system/BlockedPage";
import { GoPage } from "@/features/system/GoPage";
import { HomePage } from "@/features/home/HomePage";
import { PlotPage } from "@/features/plot/PlotPage";
import { ChatPage } from "@/features/chat/ChatPage";
import { ConversationsPage } from "@/features/conversations/ConversationsPage";
import { LoginPage } from "@/features/auth/LoginPage";
import { TermsPage } from "@/features/auth/TermsPage";
import { BirthDatePage } from "@/features/auth/BirthDatePage";
import { ProfileNewPage } from "@/features/auth/ProfileNewPage";
import { PushPermissionPage } from "@/features/push/PushPermissionPage";
import { StorePage } from "@/features/store/StorePage";
import { DonePage } from "@/features/store/DonePage";
import { WebFailPage, WebSuccessPage } from "@/features/store/web/WebSuccessPage";
import { MePage } from "@/features/me/MePage";
import { ProfileEditPage } from "@/features/me/ProfileEditPage";
import { MeEditPage } from "@/features/me/MeEditPage";
import { CouponPage } from "@/features/me/CouponPage";
import { BricksHistoryPage } from "@/features/me/BricksHistoryPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { SupportPage } from "@/features/settings/SupportPage";
import { DeletePage } from "@/features/delete/DeletePage";
import { BootstrapErrorPage } from "@/features/system/BootstrapErrorPage";

/** F01-01 최소 노출(콜드 스타트 1회) = M-1 모션 길이 + 여유. 부팅이 먼저 끝나도 모션은 끝까지 보이고, 늦으면 마지막 프레임에서 기다린다. 테스트에서는 0. */
const SPLASH_MIN_MS = import.meta.env.MODE === "test" ? 0 : SPLASH_MOTION_SECONDS * 1000 + 200;
let splashShown = false;

/** 루트 loader: 부팅을 기다린다(초기 로드 중에는 HydrateFallback = 스플래시). 점검·업데이트면 /blocked로. */
async function rootLoader({ request }: { request: Request }) {
  const minWait = splashShown || SPLASH_MIN_MS === 0 ? undefined : new Promise<void>((r) => setTimeout(r, SPLASH_MIN_MS));
  splashShown = true;
  const [{ next }] = await Promise.all([ensureBootstrapped(), minWait]);
  const path = new URL(request.url).pathname;
  if (next.startsWith("/blocked") && path !== "/blocked") return redirect(next);
  return null;
}

/**
 * 상점 라우트. 웹도 실제 상점이다(상점·결제 설계 2026-10-26 결정 10: 토스페이먼츠) — 미지원 국가 계정은 StorePage 가 결제 불가 안내로 바꾼다(결정 17).
 * store/web/success·fail 은 토스 결제창이 돌아오는 자리(웹 전용, 앱에서 열리면 상점으로).
 */
function storeRoutes(): RouteObject[] {
  const webOnly = () => (platform.clientKind === "web" ? null : redirect("/store"));
  return [
    { path: "store", element: <StorePage />, loader: requireMember },
    { path: "store/done", element: <DonePage />, loader: requireMember },
    { path: "store/web/success", element: <WebSuccessPage />, loader: (args) => webOnly() ?? requireMember(args) },
    { path: "store/web/fail", element: <WebFailPage />, loader: webOnly },
  ];
}

/** 라우트 17개(client.md 2.1). 시트·다이얼로그는 라우트가 아니다. */
export function createRoutes(): RouteObject[] {
  return [
    {
      path: "/",
      element: <AppShell />,
      loader: rootLoader,
      HydrateFallback: SplashPage,
      errorElement: <BootstrapErrorPage />,
      children: [
        // replace: "/" 가 히스토리에 남으면 홈에서 뒤로가기 → "/" → 다시 /home 으로 돌아와 앱이 종료되지 않는다(AC0-88).
        // 딥링크(플롯 등)는 홈을 replace 로 깔고 셸이 그 위에 push 한다 — 플롯이 히스토리 0번이면 뒤로가기가 앱을 종료한다(AC0-360).
        {
          index: true,
          loader: async () => {
            const { next } = await ensureBootstrapped();
            if (next === "/home" || next.startsWith("/blocked")) return replace(next);
            deferColdStartPath(next);
            return replace("/home");
          },
        },
        { path: "home", element: <HomePage /> },
        { path: "plots/:key", element: <PlotPage /> },
        { path: "chat/:key", element: <ChatPage /> },
        { path: "login", element: <LoginPage />, loader: guestOnly },
        { path: "terms", element: <TermsPage />, loader: requireOnboarding },
        { path: "birth-date", element: <BirthDatePage />, loader: requireOnboarding },
        { path: "profile/new", element: <ProfileNewPage />, loader: requireOnboarding },
        // F01-15 알림 권한 안내(AC0-260): 가입 직후 웰컴 다음 1회, 앱 전용(웹은 홈으로)
        { path: "push-permission", element: <PushPermissionPage />, loader: (args) => (platform.clientKind === "web" ? redirect("/home") : requireMember(args)) },
        ...storeRoutes(),
        { path: "chats", element: <ConversationsPage />, loader: requireMember },
        { path: "me", element: <MePage />, loader: requireMember },
        { path: "me/edit", element: <MeEditPage />, loader: requireMember },
        { path: "profile/:id/edit", element: <ProfileEditPage />, loader: requireMember },
        { path: "me/coupon", element: <CouponPage />, loader: requireMember },
        { path: "me/bricks", element: <BricksHistoryPage />, loader: requireMember },
        { path: "settings", element: <SettingsPage /> },
        { path: "support", element: <SupportPage /> },
        // 1.20: done은 DELETE /me 뒤(세션 없음)에 열리므로 회원 가드를 건너뛴다. 나머지 단계는 회원만. 웹·앱 공통(AC0-318).
        { path: "delete/:step", element: <DeletePage />, loader: (args) => (args.params.step === "done" ? null : requireMember(args)) },
        { path: "blocked", element: <BlockedPage /> },
        { path: "go", element: <GoPage /> },
        ...(import.meta.env.DEV
          ? [
              { path: "dev/gallery", lazy: async () => ({ Component: (await import("@/dev/Gallery")).Gallery }) },
              // 부팅이 빨라 실제로는 스치는 F01-01 을 시안과 대조하기 위한 개발 전용 미리보기
              { path: "dev/splash", element: <SplashPage /> },
            ]
          : []),
        { path: "*", loader: () => redirect("/home") },
      ],
    },
  ];
}
